import React, { useState, useEffect } from 'react';
import { classApi } from '../api';
import { Class } from '../api_types';
import { useAuth } from '../contexts/AuthContext';
import { format, parse } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import CreditBalance from '../components/CreditBalance';
import ClassRefund from '../components/ClassRefund';
import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Alert, AlertTitle, Typography, Button, Box } from '@mui/material';

interface MemberClassesProps {
  facilityId: string;
  businessId: string;
}

interface SuccessNotificationProps {
  classTitle: string;
  date: string;
  startTime: string;
  endTime: string;
  onClose: () => void;
}

const formatTime = (time: string) => {
  try {
    // Parse the time string (HH:mm format)
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const minute = parseInt(minutes, 10);
    
    // Create a date object at the current date with the specified time
    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    
    // Format the time in 12-hour format with AM/PM
    return format(date, 'h:mm a');
  } catch (err) {
    console.error('Error formatting time:', err);
    return time;
  }
};

const formatDate = (dateStr: string) => {
  try {
    // Parse the date string (YYYY-MM-DD format)
    const date = parse(dateStr, 'yyyy-MM-dd', new Date());
    
    // Format the date
    return format(date, 'MMMM d, yyyy');
  } catch (err) {
    console.error('Error formatting date:', err);
    return dateStr;
  }
};

// Add new function to check if a class is in the past
const isPastClass = (classItem: Class) => {
  try {
    const classDate = parse(classItem.date, 'yyyy-MM-dd', new Date());
    const [hours, minutes] = classItem.endTime.split(':');
    classDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    
    return classDate < new Date();
  } catch (err) {
    console.error('Error checking if class is past:', err);
    return false;
  }
};

const SuccessNotification: React.FC<SuccessNotificationProps> = ({
  classTitle,
  date,
  startTime,
  endTime,
  onClose,
}) => {
  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-500 bg-opacity-90 text-white px-6 py-3 rounded-lg shadow-lg z-50 max-w-2xl w-full mx-4">
      <div className="flex justify-between items-center text-sm">
        <div>
          <h3 className="font-semibold text-sm mb-0.5">Registration Successful!</h3>
          <p className="text-sm">
            You are successfully registered for {classTitle} on {formatDate(date)} from{' '}
            {formatTime(startTime)} to {formatTime(endTime)}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 ml-4 flex-shrink-0"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

const MemberLessons: React.FC<MemberClassesProps> = ({ facilityId, businessId }) => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [registeredClass, setRegisteredClass] = useState<Class | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [conflictError] = useState<string | null>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [creditRefreshCount, setCreditRefreshCount] = useState(0);
  const [showPaymentDialog, setShowPaymentDialog] = useState<{open: boolean; classItem: Class | null}>({open: false, classItem: null});
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'credits'>('card');

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        if (!facilityId || !user || !businessId) {
          setError('No facility ID or user found or business ID');
          return;
        }
        const data = await classApi.getFacilityClasses(businessId, facilityId);
        
        // Fetch registration details for each class
        const classesWithRegistrations = await Promise.all(
          data.map(async (classItem) => {
            if (classItem.registeredMembers.includes(user.uid)) {
              try {
                const registrations = await classApi.getClassRegistrations(businessId, facilityId, classItem.id);
                console.log('registrations for class', classItem.id, registrations);
                const userRegistration = registrations.find(reg => reg.memberId === user.uid);
                console.log('userRegistration for class', classItem.id, userRegistration);
                if (userRegistration) {
                  return {
                    ...classItem,
                    registration: {
                      id: userRegistration.id,
                      status: userRegistration.status
                    }
                  };
                }
              } catch (err) {
                console.error('Error fetching registration:', err);
              }
            }
            return classItem;
          })
        );
        
        setClasses(classesWithRegistrations.filter(Boolean));
      } catch (err) {
        setError('Failed to fetch classes');
        console.error('Error fetching classes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClasses();
  }, [facilityId, user]);

  useEffect(() => {
    const fetchCredits = async () => {
      if (user && facilityId) {
        try {
          const res = await classApi.getMemberCredits(user.uid, businessId, facilityId);
          setCreditBalance(res.balance);
        } catch (err) {
          setCreditBalance(null);
        }
      }
    };
    fetchCredits();
  }, [user, facilityId, creditRefreshCount]);

  // Check for successful payment on page load
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');
      const classId = params.get('class_id');

      if (sessionId && classId) {
        try {
          const { status, paymentStatus } = await classApi.checkRegistrationStatus(businessId, facilityId, classId, sessionId);
          if (status === 'registered' && paymentStatus === 'paid') {
            // Find the registered class
            const registeredClass = classes.find(c => c.id === classId);
            if (registeredClass) {
              setRegisteredClass(registeredClass);
              setShowSuccess(true);
            }
            // Refresh the classes data
            await forceFullReset();
            // Remove query parameters
            navigate('/member/dashboard/lessons', { replace: true });
          }
        } catch (err) {
          console.error('Error checking registration status:', err);
        }
      }
    };

    checkPaymentStatus();
  }, [location, navigate, classes, facilityId]);

  const forceFullReset = async () => {
    setLoading(true);
    // Small delay to allow backend to update
    await new Promise(res => setTimeout(res, 1500));
    try {
      const data = await classApi.getFacilityClasses(businessId, facilityId);
      // Fetch registration details for each class
      const classesWithRegistrations = await Promise.all(
        data.map(async (classItem) => {
          if (user && classItem.registeredMembers.includes(user.uid)) {
            try {
              const registrations = await classApi.getClassRegistrations(businessId, facilityId, classItem.id);
              const userRegistration = registrations.find(reg => reg.memberId === user.uid);
              if (userRegistration) {
                return {
                  ...classItem,
                  registration: {
                    id: userRegistration.id,
                    status: userRegistration.status
                  }
                };
              }
            } catch (err) {
              console.error('Error fetching registration:', err);
            }
          }
          return classItem;
        })
      );
      setClasses(classesWithRegistrations.filter(Boolean));
      setCreditRefreshCount((c) => c + 1); // Refresh credits after full reset
    } catch (err) {
      setError('Failed to fetch classes');
      console.error('Error fetching classes:', err);
    } finally {
      setLoading(false);
      setShowSuccess(false);
      setRegisteredClass(null);
      setShowPaymentDialog({ open: false, classItem: null });
      setSelectedClass(null);
      setShowConfirmation(false);
    }
  };

  const handleRegister = async (classItem: Class) => {
    if (!user) return;

    // If class has a price, check credits and show payment dialog if needed
    if (classItem.price && classItem.price > 0) {
      setShowPaymentDialog({ open: true, classItem });
      return;
    }

    // Free class, register directly
    try {
      await classApi.registerWithCredits(businessId, facilityId, classItem.id, user.uid);
      await forceFullReset();
      setShowSuccess(true);
      setRegisteredClass(classItem);
    } catch (err) {
      setError('Failed to register');
      console.error('Error registering:', err);
    }
  };

  const handlePayment = async () => {
    if (!user || !showPaymentDialog.classItem) return;
    const classItem = showPaymentDialog.classItem;

    try {
      if (paymentMethod === 'credits') {
        const price = classItem.price || 0;
        const credits = creditBalance ?? 0;
        if (credits >= price) {
          // Full credit payment
          await classApi.registerWithCredits(businessId, facilityId, classItem.id, user.uid);
          await forceFullReset();
          setShowSuccess(true);
          setRegisteredClass(classItem);
          setShowPaymentDialog({ open: false, classItem: null });
        } else if (credits > 0) {
          // Mixed payment: use all credits, pay remainder with card
          const remainingAmount = price - credits;
          const successUrl = `${window.location.origin}/member/dashboard/lessons?session_id={CHECKOUT_SESSION_ID}&class_id=${classItem.id}`;
          const cancelUrl = `${window.location.origin}/member/dashboard/lessons`;
          const { url } = await classApi.createClassCheckout(
            businessId,
            facilityId,
            classItem.id,
            user.uid,
            successUrl,
            cancelUrl,
            true, // useCredits
            credits // creditAmount
          );
          setShowPaymentDialog({ open: false, classItem: null });
          window.location.href = url;
        } else {
          setError('Insufficient credits for this class.');
        }
      } else {
        // Card payment flow
        const successUrl = `${window.location.origin}/member/dashboard/lessons?session_id={CHECKOUT_SESSION_ID}&class_id=${classItem.id}`;
        const cancelUrl = `${window.location.origin}/member/dashboard/lessons`;
        const { url } = await classApi.createClassCheckout(
          businessId,
          facilityId,
          classItem.id,
          user.uid,
          successUrl,
          cancelUrl,
          false, // useCredits
          0      // creditAmount
        );
        setShowPaymentDialog({ open: false, classItem: null });
        window.location.href = url;
      }
    } catch (err) {
      setError('Failed to register');
      console.error('Error registering:', err);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setRegisteredClass(null);
  };

  const isEnrolled = (classItem: Class) => {
    return user?.uid ? classItem.registeredMembers.includes(user.uid) : false;
  };

  const handleRefundSuccess = async () => {
    // Refresh the classes data and credit balance after successful refund
    try {
      await forceFullReset();
      setCreditRefreshCount((c) => c + 1); // Refresh credits
    } catch (err) {
      console.error('Error refreshing classes or credits:', err);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div>Error: {error}</div>;

  // Filter out past classes
  const enrolledClasses = classes
    .filter(classItem => isEnrolled(classItem) && !isPastClass(classItem));
  const availableClasses = classes
    .filter(classItem => !isEnrolled(classItem) && !isPastClass(classItem));

  return (
    <div className="container mx-auto px-4 py-8">
      {showSuccess && registeredClass && (
        <SuccessNotification
          classTitle={registeredClass.title}
          date={registeredClass.date}
          startTime={registeredClass.startTime}
          endTime={registeredClass.endTime}
          onClose={handleCloseSuccess}
        />
      )}
      
      {conflictError && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {conflictError}
        </div>
      )}

      {/* Add Credit Balance Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Lessons and Classes</h1>
              <p className="text-gray-600 mt-1">View and register for upcoming lessons and classes</p>
            </div>
            {user && facilityId && (
              <CreditBalance memberId={user.uid} businessId={businessId} facilityId={facilityId} key={creditRefreshCount} />
            )}
          </div>
        </div>
      </div>
      
      {enrolledClasses.length > 0 && (
        <div className="mb-12">
          <h1 className="text-2xl font-bold mb-6">My Upcoming Lessons</h1>
          <div className="flex flex-col space-y-4">
            {enrolledClasses.map((classItem) => {
              if (classItem.registration) {
                console.log('Passing registrationId to ClassRefund:', classItem.registration.id, 'for class', classItem.id);
              }
              return (
                <div key={classItem.id} className="bg-white p-4 rounded-lg shadow">
                  <h3 className="text-lg font-semibold">{classItem.title}</h3>
                  <p className="text-gray-600">{classItem.description}</p>
                  <div className="mt-2 text-sm text-gray-500">
                    <p>Date: {formatDate(classItem.date)}</p>
                    <p>Time: {formatTime(classItem.startTime)} - {formatTime(classItem.endTime)}</p>
                    <p>Capacity: {classItem.registeredMembers.length}/{classItem.maxCapacity}</p>
                  </div>
                  <div className="mt-4">
                    {classItem.registration && (
                      <ClassRefund
                        classItem={classItem}
                        registrationId={classItem.registration.id}
                        facilityId={facilityId}
                        businessId={businessId}
                        onSuccess={handleRefundSuccess}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <h1 className="text-2xl font-bold mb-6"> Lessons and Classes </h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {availableClasses.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-16 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0v6m0 0H6m6 0h6" />
            </svg>
            <p className="text-lg font-medium">No available lessons or classes at this time.</p>
            <p className="text-sm mt-2">Please check back later!</p>
          </div>
        ) : (
          availableClasses.map((classItem) => (
            <div key={classItem.id} className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-2">{classItem.title}</h2>
              <p className="text-gray-600 mb-4">{classItem.description}</p>
              
              <div className="space-y-2 mb-4">
                <p>
                  <span className="font-medium">Date:</span>{' '}
                  {formatDate(classItem.date)}
                </p>
                <p>
                  <span className="font-medium">Time:</span>{' '}
                  {formatTime(classItem.startTime)} - {formatTime(classItem.endTime)}
                </p>
                <p>
                  <span className="font-medium">Price:</span> ${classItem.price}
                </p>
                <p>
                  <span className="font-medium">Capacity:</span>{' '}
                  {classItem.registeredMembers.length}/{classItem.maxCapacity}
                </p>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleRegister(classItem)}
                  disabled={classItem.registeredMembers.length >= classItem.maxCapacity || isEnrolled(classItem)}
                  className={`py-2 px-4 rounded ${
                    classItem.registeredMembers.length >= classItem.maxCapacity || isEnrolled(classItem)
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {classItem.registeredMembers.length >= classItem.maxCapacity
                    ? 'Class Full'
                    : isEnrolled(classItem)
                    ? 'Already Enrolled'
                    : 'Register'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showConfirmation && selectedClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Confirm Registration</h2>
            <p className="mb-4">
              You are about to register for {selectedClass.title} on{' '}
              {formatDate(selectedClass.date)} from{' '}
              {formatTime(selectedClass.startTime)} to{' '}
              {formatTime(selectedClass.endTime)}.
            </p>
            <p className="mb-4">Price: ${selectedClass.price}</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Proceed to Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Dialog for classes */}
      <Dialog
        open={showPaymentDialog.open}
        onClose={() => setShowPaymentDialog({ open: false, classItem: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'primary.main',
          color: 'white',
          '& .MuiTypography-root': {
            fontSize: '1.5rem',
            fontWeight: 600,
          },
        }}>
          Complete Registration
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
              Class Details
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'background.default', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {showPaymentDialog.classItem?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(showPaymentDialog.classItem?.date || '')} at {formatTime(showPaymentDialog.classItem?.startTime || '')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
              Payment Summary
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'background.default', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Class Price</Typography>
                <Typography variant="body1" fontWeight={500}>${showPaymentDialog.classItem?.price}</Typography>
              </Box>
              {paymentMethod === 'credits' && (creditBalance ?? 0) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">Credits Applied</Typography>
                  <Typography variant="body1" color="success.main">-${Math.min(creditBalance ?? 0, showPaymentDialog.classItem?.price || 0)}</Typography>
                </Box>
              )}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 2, 
                pt: 2, 
                borderTop: '1px solid',
                borderColor: 'divider',
              }}>
                <Typography variant="body1" fontWeight={600}>Total Due</Typography>
                <Typography variant="body1" fontWeight={600} color="primary.main">
                  ${paymentMethod === 'credits' 
                    ? Math.max(0, (showPaymentDialog.classItem?.price || 0) - (creditBalance ?? 0))
                    : showPaymentDialog.classItem?.price}
                </Typography>
              </Box>
            </Box>
          </Box>

          {creditBalance !== null && (creditBalance ?? 0) > 0 && (
            <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
              <FormLabel component="legend" sx={{ 
                color: 'text.primary',
                fontWeight: 600,
                mb: 1,
              }}>
                Payment Method
              </FormLabel>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as 'card' | 'credits')}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value="card"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>Credit Card</Typography>
                      <Box component="img" src="/credit-card-icon.png" sx={{ height: 24 }} />
                    </Box>
                  }
                  sx={{
                    border: '1px solid',
                    borderColor: paymentMethod === 'card' ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 1,
                    m: 0,
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                />
                <FormControlLabel
                  value="credits"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>Credits</Typography>
                      <Typography variant="body2" color="success.main">
                        (Balance: ${(creditBalance ?? 0).toFixed(2)})
                      </Typography>
                    </Box>
                  }
                  sx={{
                    border: '1px solid',
                    borderColor: paymentMethod === 'credits' ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 1,
                    m: 0,
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                />
              </RadioGroup>
            </FormControl>
          )}

          {paymentMethod === 'credits' && (creditBalance ?? 0) < (showPaymentDialog.classItem?.price || 0) && (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 2,
                '& .MuiAlert-icon': {
                  color: 'info.main',
                },
              }}
            >
              <AlertTitle>Mixed Payment</AlertTitle>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You'll use ${(creditBalance ?? 0).toFixed(2)} in credits and pay the remaining ${((showPaymentDialog.classItem?.price || 0) - (creditBalance ?? 0)).toFixed(2)} with a credit card.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 2,
        }}>
          <Button 
            onClick={() => setShowPaymentDialog({ open: false, classItem: null })}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePayment}
            variant="contained"
            color="primary"
            disabled={paymentMethod === 'credits' && (creditBalance ?? 0) <= 0}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 200,
            }}
          >
            {paymentMethod === 'credits' 
              ? (creditBalance ?? 0) >= (showPaymentDialog.classItem?.price || 0)
                ? 'Pay with Credits'
                : `Pay $${((showPaymentDialog.classItem?.price || 0) - (creditBalance ?? 0)).toFixed(2)} with Card`
              : 'Proceed to Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MemberLessons; 